import { defineStore } from 'pinia'
import { computed } from 'vue'
import { useNuxtApp } from '#app'
import { useSyncData } from '@/composables/useSyncData'
import { useAuthStore } from '@/stores/useAuthStore'
import type { Product } from '@/_api/models/product'

const listOrder = (item: { listOrder?: number } | null | undefined): number => {
  if (item?.listOrder == null) return Number.MAX_SAFE_INTEGER

  const value = Number(item?.listOrder)
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER
}

const compareListOrder = (
  left: { listOrder?: number } | null | undefined,
  right: { listOrder?: number } | null | undefined,
): number => listOrder(left) - listOrder(right)

export const useProductStore = defineStore('product', () => {
  const { $api, $grants } = useNuxtApp()
  const authStore = useAuthStore()

  const accountId = computed(() => authStore.activeGuid)

  const {
    items: products,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    sync: _sync,
    findById,
  } = useSyncData<Product>({
    service: $api.product,
    key: 'products',
    accountId,
    grants: { product: ['view'] },
  })

  const sync = async (options?: { force?: boolean; change?: boolean }) => {
    if (!$grants.all({ product: ['view'] })) {
      return
    }

    await _sync(options)
  }

  const syncDate = computed(() => syncState.value.date)

  const all = computed(() => {
    const grouped = new Map<string, { category: any, products: Product[] }>()

    for (const product of products.value) {
      const categoryId = product.category?.idProductCategory?.toString() ?? 'uncategorised'

      if (!grouped.has(categoryId)) {
        grouped.set(categoryId, { category: product.category, products: [] })
      }

      grouped.get(categoryId)!.products.push(product)
    }

    return Array.from(grouped.values())
      .map(group => ({
        ...group,
        products: [...group.products].sort(compareListOrder),
      }))
      .sort((left, right) => compareListOrder(left.category, right.category))
  })

  return {
    products,
    count,
    hydrated,
    syncState,
    isSyncing,
    syncProgress,
    syncDate,
    sync,
    findById,
    all,
  }
})
